import {
	useState,
	useEffect,
	useRef
} from "react";
import {
	Box,
	Typography,
	Grid,
	Paper,
	IconButton,
	TextField,
	Checkbox,
	Tooltip,
	CircularProgress,
	FormControlLabel,
	Divider,
	Switch, MenuItem, Menu, Link,
	Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab'
import SectionTitle from '../../components/common/SectionTitle'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CurrentClientContext from '../../providers/CurrentClientProvider'
import ClientService from '../../services/ClientService'
import moment from 'moment'
import TipsContext from '../../providers/GlobalTip'
import EmptyIcon from '../../components/icons/EmptyIcon'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DataFileService from '../../services/DataFileService'
import SettingsIcon from '@mui/icons-material/Settings';
import SettingModal from '../../components/client/uploadData/SettingModal'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AuthProvider from "../../providers/AuthProvider";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { trackEvent, handleTrackError } from "../../services/AmplitudeService";
import DateEditModal from '../../components/client/uploadData/DateEditModal';
import DataTable from "../../components/client/uploadData/DataTable.component";

const jss = {
	changeLogItem: {
		display:'flex',
		p:1,
		border:'1px solid #ccc',
		borderRadius:1,
		mt:1,
		transition: 'all 0.3s',
		info: {
			flex:1,
			wordBreak:'break-all',
			cursor:'pointer',
		},
		name: {
			whiteSpace:"wrap",
			fontSize:13,
		},
		extra: {
			fontSize:11,
			mt:0.5,
		},
		checkIcon: {
			ml:1,
			cursor:'pointer',
		},
		checkIconHead: {
			ml:1,
			cursor:'pointer',
			color: 'white',
			fontSize: '20px'
		},
		":hover": {
			backgroundColor: '#f3f4f5',
		},
		'&.checked': {
			backgroundColor: '#dbcfe6',
			color:'primary.main',
			borderColor: 'primary.main'
		},
	},
	emptySelectLog: {
		textAlign:'center', 
		py:15, 
		color:'text.disabled', 
		fontSize:30
	},
	changeLogSection: {
		title: {
			fontSize:16,
			fontWeight:'bold',
			my:2
		},
		actionSection: {
			p:2,
			backgroundColor: '#eee9f3',
			display:'flex',
			alignItems:'center',
		},
	},
}

function getUploadDataJss(visualVariant) {
	if( visualVariant !== 'v3' ) {
		return jss
	}

	return {
		...jss,
		changeLogItem: {
			...jss.changeLogItem,
			border: '1px solid #eaecf0',
			borderRadius: '18px',
			background: 'linear-gradient(180deg, #ffffff 0%, #fcfbff 100%)',
			boxShadow: '0 8px 18px rgba(16, 24, 40, 0.06)',
			":hover": {
				backgroundColor: '#f9f5ff',
			},
			'&.checked': {
				background: 'linear-gradient(180deg, #f9f5ff 0%, #f4ebff 100%)',
				color:'#53389e',
				borderColor: '#b692f6',
				boxShadow: '0 10px 20px rgba(127, 86, 217, 0.12)',
			}
		},
		emptySelectLog: {
			...jss.emptySelectLog,
			color:'#98a2b3',
		},
	}
}

function UploadData(props) {
	const isV3 = props?.visualVariant === 'v3'
	const styles = getUploadDataJss(props?.visualVariant)
	const auth = AuthProvider.useAuth();
	const fileInputRef = useRef()
	const [dataFiles, setDataFiles] = useState([])
	const [loading, setLoading] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [loadingSelect, setLoadingSelect] = useState(false)
	const currentClientContext = CurrentClientContext.useClient()
	const [targetDataFile, setTargetDataFile] = useState()
	const tip_context = TipsContext.useMessage()
	const [uploadDataForm, setUploadDataForm] = useState({
		isEndOfMonth: false,
		dataThroughDate: moment().subtract(1, 'day'),
	})
	const [settingOpen, setSettingOpen] = useState(false)
	const [togglingEndOfMonth, setTogglingEndOfMonth] = useState(false)
	const [isFinalizing, setIsFinalizing] = useState(false)
	const [isAdmin, setIsAdmin] = useState(false)

	const more_element = useRef()
	const [open_more, setOpenMore] = useState(false)
	const [dateEditModalOpen, setDateEditModalOpen] = useState(false)

	useEffect(() => {
		document.title = 'Retail ORBIT: Client Data Files'
	}, [])

	useEffect(() => {
		if( currentClientContext.client ) {
			loadDataFiles()
		}
	}, [currentClientContext.client])

	useEffect(() => {
		if( auth.checkPermission('role-create') ) {
			setIsAdmin(true)
		}
	}, [auth.user])

	async function loadDataFiles() {
		const _files = await DataFileService.getClientDataFiles(currentClientContext.client.Id)
		setLoading(false)
		setDataFiles(_files)
	}

	async function selectDataFile(dataFile) {
		setLoadingSelect(true)
		setTargetDataFile(dataFile)
		setLoadingSelect(false)
	}

	function chooseFile() {
		fileInputRef.current.click()
	}

	/**
	 * Handles on change for is end of month checkbox on single files
	 *
	 * @param {Event} event
	 *
	 * @return {void}
	 */
	async function changeDataFileEndOfMonth(event) {
		setTogglingEndOfMonth(true)
		try {
			let result = await DataFileService.updateDataFile(currentClientContext.client.Id, targetDataFile.ClientDataFileId, {
				IsMonthToDate: event.target.checked ? 0 : 1 // inverted because of wording on the checkbox
			})
			dataFileChangeHandler(result)
			tip_context.showSuccess("Data file updated")

			trackEvent('Client Data Files Event', {
				action: 'Toggled End of Month Data File',
				epochMonth: ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate()),
				clientCode: currentClientContext.client.ClientCode,
				isEndOfMonth: event.target.checked,
			})
		} catch(e) {
			console.log(e)
			tip_context.show("Something went wrong marking the data file as end of month")

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Toggle End of Month Data File' } })
		}
		setTogglingEndOfMonth(false)
	}

	/**
	 * Finalize/Unfinalize target file
	 *
	 * @params {boolean} finalize
	 *
	 * @return {void}
	 */
	async function setFinalizeFile(finalize) {
		if (!targetDataFile) {
			return;
		}
		if (finalize && targetDataFile.IsMonthToDate) {
			tip_context.show("Only end of month files can be finalized")
			return
		}
		if (isFinalizing) {
			return
		}
		setIsFinalizing(true)
		try {
			let result;
			if( finalize ) {
				result = await DataFileService.finalizeDataFile(currentClientContext.client.Id, targetDataFile.ClientDataFileId)
			} else {
				result = await DataFileService.unfinalizeDataFile(currentClientContext.client.Id, targetDataFile.ClientDataFileId)
			}

			tip_context.showSuccess("Data file updated")

			console.log(result)

			dataFileChangeHandler(result)

			if (finalize) {
				trackEvent('Client Data Files Event', {
					action: 'Finalized Data File',
					epochMonth: ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate()),
					clientCode: currentClientContext.client.ClientCode,
				})
			} else {
				trackEvent('Client Data Files Event', {
					action: 'Unfinalized Data File',
					epochMonth: ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate()),
					clientCode: currentClientContext.client.ClientCode,
				})
			}
		} catch(e) {
			tip_context.show("Something went wrong finalizing the data file")

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Toggle Finalize Data File' } })
		}
		setIsFinalizing(false)
	}

	async function uploadDataFile(e) {
		if( e.target.files.length==0 ) {
			return
		}
		try {
			setUploading(true)
			let data = {
				is_month_to_date: uploadDataForm.isEndOfMonth ? 0 : 1,
			}
			if( !uploadDataForm.isEndOfMonth ) {
				data['data_through_date'] = uploadDataForm.dataThroughDate.format('YYYY-MM-DD 00:00:00')
			}
			const new_data_file = await DataFileService.uploadDataFile(currentClientContext.client.Id, e.target.files[0], data)
			dataFileChangeHandler()

			trackEvent('Client Data Files Event', {
				action: 'Uploaded Data File',
				epochMonth: ClientService.getEpochMonthFromDate(moment(new_data_file.Period + '01').toDate()),
				clientCode: currentClientContext.client.ClientCode,
				monthToDate: new_data_file.IsMonthToDate,
				fileType: e.target.files[0].type,
			})
		} catch(e) {
			if (e.response?.data?.message) {
				tip_context.show(e.response?.data.message)
			} else {
				tip_context.show("Something went wrong uploading file")
			}

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Upload Data File' } })
		}
		setUploading(false)
	}

	function dataFileChangeHandler(_updatedDataFile) {
		loadDataFiles()
		if( _updatedDataFile ) {
			setTargetDataFile(_updatedDataFile)	
		}
	}

	function dataFileDeleteHandler(_deletedDataFile) {
		loadDataFiles()
		setTargetDataFile(null)
	}

	function openSettingModal() {
		setSettingOpen(true)
	}

	function closeSettingModal() {
		setSettingOpen(false)
	}

	function openDateEditModal() {
		setDateEditModalOpen(true)
	}

	function closeDateEditModal() {
		setDateEditModalOpen(false)
	}

	function handleDateUpdate(updatedDataFile) {
		dataFileChangeHandler(updatedDataFile)
		trackEvent('Client Data Files Event', {
			action: 'Updated Data Through Date',
			epochMonth: ClientService.getEpochMonthFromDate(moment(updatedDataFile.Period + '01').toDate()),
			clientCode: currentClientContext.client.ClientCode,
		})
	}

	async function backup() {
		try {
			await DataFileService.clientDataFileBackup(currentClientContext.client.Id)

			trackEvent('Client Data Files Event', {
				action: 'Backed up data',
				clientCode: currentClientContext.client.ClientCode,
			})
		} catch(e) {
			tip_context.show("Something went wrong backing up data")

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Backup Data' } })
		}
		// DataFileService.clientDataFileBackup(currentClientContext.client.Id)
	}

	async function syncCloudData() {
		try {
			await DataFileService.syncCloudData(currentClientContext.client.Id)
		} catch(e) {
		}
		tip_context.showSuccess()
	}
	// const currentEpoch = ClientService.getEpochMonthFromDate(new Date());

	const downloadDataTemplate = async () => {
		try {
			await DataFileService.downloadDataTemplate(currentClientContext.client.Id, currentClientContext.client.ClientCode)

			trackEvent('Client Data Flies Event', {
				action: 'Downloaded Data Template',
				clientCode: currentClientContext.client.ClientCode,
			})
		} catch (e) {
			// console.log(e)

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Download Data Template' } })
		}
	}

	const downloadFile = async () => {
		if (!targetDataFile) return false;
		setOpenMore(false)
		try {
			let response = await DataFileService.downloadDataSource(currentClientContext.client.Id, targetDataFile.Period, targetDataFile.FileName)
			const url = window.URL.createObjectURL(new Blob([response]));
			const link = document.createElement("a");
			link.href = url;

			link.setAttribute("download", targetDataFile.FileName);
			document.body.appendChild(link);
			link.click();

			// Clean up and remove the link
			link.parentNode.removeChild(link);

			trackEvent('Client Data Files Event', {
				action: 'Downloaded Data File',
				epochMonth: ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate()),
				clientCode: currentClientContext.client.ClientCode,
				fileType: targetDataFile.FileName.split('.').pop(),
			})
		} catch (e) {
			tip_context.show("Something went wrong downloading file")

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Download Data File' } })
		}
	}

	const deleteFile = async () => {
		if (!targetDataFile) return false;
		if (!window.confirm(`Are You Sure You want to Delete File ${targetDataFile.Name}`)) return false;
		setOpenMore(false)
		setLoading(true)

		try {
			let response = await DataFileService.deleteDataFile(currentClientContext.client.Id, targetDataFile.ClientDataFileId)
			setTargetDataFile(null)
			await loadDataFiles()

			trackEvent('Client Data Files Event', {
				action: 'Deleted Data File',
				epochMonth: ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate()),
				clientCode: currentClientContext.client.ClientCode,
			})
		} catch (e) {
			if (e.response?.data?.message) {
				tip_context.show(e.response.data.message)
			} else {
				tip_context.show("Something went wrong deleting file")
			}

			handleTrackError({ error: e, pageName: 'Client Data Files', additionalMetadata: { clientCode: currentClientContext.client.ClientCode, action: 'Delete Data File' } })
		}

	}

	return <Box className={isV3 ? 'v3-data-page v3-data-upload-page' : undefined} sx={{py:isV3 ? 0 : 2}}>
		<Typography variant="h5" className={isV3 ? 'v3-data-page__heading' : undefined}>Import Data</Typography>
		<Grid container spacing={2} sx={{mt:0}} className={isV3 ? 'v3-data-upload-grid' : undefined}>
			<Grid item xs={12} sm={3}>
				<Paper elevation={isV3 ? 0 : 2} className={isV3 ? 'v3-data-card v3-data-upload-card' : undefined}>
					<SectionTitle
						label="Import Data"
						customStyles={isV3 ? { background: 'linear-gradient(180deg, #f8fafc 0%, #f9f5ff 100%)', borderBottomColor: '#eaecf0' } : {}}
						customTitleStyle={isV3 ? { fontSize: 14, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' } : {}}
						rightComponent={
							<>
								<Tooltip title="Sync cloud data">
									<IconButton onClick={syncCloudData}>
										<CloudSyncIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Download Data Template">
									<IconButton onClick={downloadDataTemplate}>
										<DownloadRoundedIcon />
									</IconButton>
								</Tooltip>
							</>
						}
					/>
					<Box >
						<Box sx={{p:1.5}}>
							<FormControlLabel control={<Switch value={uploadDataForm.isEndOfMonth} onChange={e => setUploadDataForm({...uploadDataForm, isEndOfMonth: e.target.checked})} />} label="Is end of month?" />
							{!uploadDataForm.isEndOfMonth &&
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DesktopDatePicker
										label="Data through date"
										value={uploadDataForm.dataThroughDate}
										inputFormat="MMM dd"
										mask=""
										onChange={e => setUploadDataForm({...uploadDataForm, dataThroughDate: moment(e)})}
										renderInput={(params) => <TextField {...params}
											fullWidth={true}
											variant="outlined"
											size="small"
											sx={{mt:2}} 
										/>}
									/>
								</LocalizationProvider>
							}
							<Box 
								component="input" 
								type='file' 
								name="data" 
								sx={{display:'none'}} 
								ref={fileInputRef}
								onChange={uploadDataFile}
								onClick={(event)=> {
									event.target.value = null
								}}
							></Box>
							<LoadingButton loading={uploading} variant="contained" fullWidth startIcon={<CloudUploadRoundedIcon />} onClick={chooseFile} sx={{mt:2}}>Upload File</LoadingButton>
						</Box>
						<Divider sx={{mt:1}}/>
						<Box sx={{p:1.5}}>
							<Box sx={{
								display:'flex', 
								justifyContent:'space-between',
								alignItems: 'center',
							}}>
								<Typography variant="body1">Data Files</Typography>
								<Box>
									<Tooltip title="Backup">
										<IconButton onClick={backup}>
											<CloudDownloadOutlinedIcon />
										</IconButton>
									</Tooltip>
									{isAdmin ? 
										<>
										<Tooltip title="Adjust inventory">
											<IconButton onClick={openSettingModal}>
												<SettingsIcon />
											</IconButton>
										</Tooltip>
										<SettingModal 
											client={currentClientContext.client}
											open={settingOpen}
											onClose={closeSettingModal}
											onRefreshDataFiles={dataFileChangeHandler}
										/>
										</>
									: null}
								</Box>
							</Box>
										<Box sx={{maxHeight:'80vh',overflowY:'scroll'}} className={isV3 ? 'v3-data-file-list' : undefined}>
							{dataFiles?.length>0 && dataFiles.map(_file => {
												return <Box key={`data_file_${_file.ClientDataFileId}`} sx={styles.changeLogItem} className={`${isV3 ? 'v3-data-file-row ' : ''}${targetDataFile?.ClientDataFileId==_file.ClientDataFileId ? 'checked' : ''}`} >
									<Box sx={jss.changeLogItem.info} onClick={e => selectDataFile(_file)}>
										<Box sx={jss.changeLogItem.name}>
											<b>{moment(_file.Period, 'YYYYMM').format('MMM YYYY')}</b>
											<Box component="span" sx={{ml:1}}>{_file.Name}</Box>
										</Box>
										<Box sx={jss.changeLogItem.extra}>
											{_file.IsMonthToDate==1 && <>
												{moment(_file.DataThroughDate).format('M/DD')}
											</>}
											{_file.IsMonthToDate==0 && <>
												End of Month
											</>}
											{_file.ProcessedDate &&
												<Box component="span" sx={{ml:1}}>Updated: {moment(_file.ProcessedDate).format('M/DD HH:mm')}</Box>
											}
										</Box>
									</Box>
                                    {!!_file.is_month_to_date && 
                                    	<>MTD</>
                                    }
									{!_file.is_month_to_date && <>
										{!!_file.FinalizedDate && <>
											{!!_file.is_planned && <>
												<LockIcon sx={jss.changeLogItem.checkIcon}/>
											</>}
											{!_file.is_planned && <>
												<LockOutlinedIcon sx={jss.changeLogItem.checkIcon}/>
											</>}
										</>}
										{!_file.FinalizedDate && <>
											<LockOpenIcon sx={jss.changeLogItem.checkIcon}/>
										</>}
									</>}
								</Box>
							})}
							{loading && <Box sx={{textAlign:'center',pt:2}}>
								<CircularProgress color="primary" />
							</Box>
							}
							{dataFiles?.length===0 && !loading && 
								<Box sx={{py:2,textAlign:'center',color:'text.disabled'}} className={isV3 ? 'v3-data-preview-empty' : undefined}><EmptyIcon sx={{fontSize:60}}/></Box>
							}
							</Box>
						</Box>
					</Box>
				</Paper>
			</Grid>
			<Grid item xs={12} sm={9}>
				<Paper elevation={isV3 ? 0 : 2} className={isV3 ? 'v3-data-card v3-data-preview-card' : undefined}>
					{(!targetDataFile && !loadingSelect) &&
						<Box sx={styles.emptySelectLog} className={isV3 ? 'v3-data-preview-empty' : undefined}>
							Please select a data file
						</Box>
					}
					{loadingSelect && 
						<Box sx={styles.emptySelectLog} className={isV3 ? 'v3-data-preview-empty' : undefined}>
							<CircularProgress color="primary" />
						</Box>
					}
					{targetDataFile && currentClientContext && currentClientContext.client && (
						<>
							<Paper elevation={isV3 ? 0 : 1} className={isV3 ? 'v3-data-card' : undefined}>
								<SectionTitle label={'Data File'}
											  subLabel={'[' + moment(targetDataFile.Period, 'YYYYMM').format('MMM YYYY') + ']'}
											  type="primary"
										  customStyles={isV3 ? { background: 'linear-gradient(90deg, #7f56d9 0%, #9e77ed 100%)', borderBottomColor: '#7f56d9' } : {}}
										  customTitleStyle={isV3 ? { fontWeight: 800, letterSpacing: '0.02em' } : {}}
											  rightComponent={
													<Box sx={{display: 'flex', justifyContent: 'center'}}>
														<Box sx={{marginY: 'auto', height: 'fit-content', display: 'flex', justifyContent: 'center'}}>
															<FormControlLabel sx={{ height: 'fit-content', margin: 0, padding: 0, color: 'white' }} control={<Checkbox checked={targetDataFile.IsMonthToDate == 0} sx={{ margin: 0, padding: 0, paddingRight: '5px' }} onChange={changeDataFileEndOfMonth} disabled={togglingEndOfMonth || targetDataFile.FinalizedDate} />} label="Is end of month?" />
															{targetDataFile.IsMonthToDate == 1 && !targetDataFile.FinalizedDate && (
																<Tooltip title="Edit Data Through Date">
																	<IconButton onClick={openDateEditModal} sx={{marginLeft: '10px', marginTop: '-8px'}}>
																		<EditIcon sx={{color: 'white', fontSize: '20px'}} />
																	</IconButton>
																</Tooltip>
															)}
															{!targetDataFile.FinalizedDate ?
																<LockOpenIcon sx={jss.changeLogItem.checkIconHead} fill='white' onClick={() => {setFinalizeFile(true)}} />
																: <LockIcon sx={jss.changeLogItem.checkIconHead} onClick={() => {setFinalizeFile(false)}}/>
															}
														</Box>
														<IconButton onClick={e => {setOpenMore(true)}} ref={more_element} sx={{marginLeft: '20px'}}>
															<MoreVertOutlinedIcon sx={{color: 'white'}} />
														</IconButton>
														<Menu
															open={open_more}
															anchorEl={more_element.current}
															onClose={e => {setOpenMore(false)}}
														>
															<MenuItem dense onClick={downloadFile}>Download Original File</MenuItem>
															{targetDataFile.file_path && targetDataFile.file_path.indexOf('client_dat_imports') === -1 && targetDataFile.file_path.indexOf('client_sku_imports') === -1 && isAdmin ?
																<MenuItem dense onClick={deleteFile}>Delete File</MenuItem> : ''
															}
														</Menu>
													</Box>
											  }
								/>
                                <DataTable epochMonth={ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate())} client={currentClientContext.client} dataFile={targetDataFile} />
							</Paper>
						</>
					)}
				</Paper>
			</Grid>
		</Grid>

		{/* Date Edit Modal */}
		<DateEditModal
			open={dateEditModalOpen}
			onClose={closeDateEditModal}
			dataFile={targetDataFile}
			clientId={currentClientContext.client?.Id}
			onUpdate={handleDateUpdate}
		/>
	</Box>
}
export default UploadData
